// @file Action history manager
import { asciiSafeStringify } from '@@/bits/json_stringify'
import { safeSessionStorage } from '@@/bits/safe_storage'
import type { DraftPost } from '@@/types'
import useLogger from '@@/vuecomposables/logger'

export const logger = useLogger('ActionHistoryManager', { logLifecycleHooks: false, log: true })

const ACTION_HISTORY_KEY = 'ww_ah'
const DEFAULT_EXPIRATION_TIME = 24 * 60 * 60 * 1000 // 24 hours in milliseconds

enum ActionHistoryType {
  DELETE_DRAFT = 'DELETE_DRAFT',
}

interface BaseActionHistoryRecord {
  type: ActionHistoryType
  timestamp: number
  data: any
  expiresAt: number
}

interface DeleteDraftActionHistory extends BaseActionHistoryRecord {
  type: ActionHistoryType.DELETE_DRAFT
  data: DraftPost
}

type ActionHistoryRecord = DeleteDraftActionHistory

interface ActionHistory {
  undoStack: ActionHistoryRecord[]
  redoStack: ActionHistoryRecord[]
}

const isValidActionHistoryRecord = (action: any): action is ActionHistoryRecord => {
  if (action === null || typeof action !== 'object') return false

  // properties must be present
  if (!('type' in action) || !('timestamp' in action) || !('data' in action) || !('expiresAt' in action)) return false

  // type must be valid
  if (!Object.values(ActionHistoryType).includes(action.type)) return false

  // timestamps must be numbers
  if (typeof action.timestamp !== 'number' || typeof action.expiresAt !== 'number') return false

  // must not have expired
  if (action.expiresAt < Date.now()) return false

  return true
}

/**
 * Get the action history from the session storage. This will filter out invalid and expired actions.
 * Using this guarantees that the provided action history is valid and actionable.
 * @returns The action history
 */
const getActionHistory = (): ActionHistory => {
  const stored = safeSessionStorage.getItem(ACTION_HISTORY_KEY)
  if (stored !== null && stored !== '') {
    try {
      const history = JSON.parse(stored)

      // Filter out expired actions
      const validUndoActions = history.undoStack.filter(isValidActionHistoryRecord)
      const validRedoActions = history.redoStack.filter(isValidActionHistoryRecord)

      // If actions were filtered out, save the updated history
      if (validUndoActions.length < history.undoStack.length || validRedoActions.length < history.redoStack.length) {
        history.undoStack = validUndoActions
        history.redoStack = validRedoActions
        saveActionHistory(history)
      }

      return history
    } catch (e) {
      logger.error('Failed to parse action history:', e)
    }
  }
  return { undoStack: [], redoStack: [] }
}

const saveActionHistory = (history: ActionHistory): void => {
  safeSessionStorage.setItem(ACTION_HISTORY_KEY, asciiSafeStringify(history))
}

const recordActionHistory = (type: ActionHistoryType, data: any): void => {
  const history = getActionHistory()

  // When a new action is performed, clear the redo stack
  history.redoStack = []

  const now = Date.now()
  const newAction: ActionHistoryRecord = {
    type,
    timestamp: now,
    data,
    expiresAt: now + DEFAULT_EXPIRATION_TIME,
  }

  history.undoStack.push(newAction)

  saveActionHistory(history)
}

const undo = (): ActionHistoryRecord | null => {
  const history = getActionHistory()
  if (history.undoStack.length === 0) return null
  const action = history.undoStack.pop() as ActionHistoryRecord
  history.redoStack.push(action)
  saveActionHistory(history)
  return action
}

const redo = (): ActionHistoryRecord | null => {
  const history = getActionHistory()
  if (history.redoStack.length === 0) return null
  const action = history.redoStack.pop() as ActionHistoryRecord
  history.undoStack.push(action)
  saveActionHistory(history)
  return action
}

const clearActionHistory = (): void => {
  saveActionHistory({ undoStack: [], redoStack: [] })
}

export {
  ACTION_HISTORY_KEY,
  DEFAULT_EXPIRATION_TIME,
  ActionHistoryType,
  clearActionHistory,
  getActionHistory,
  recordActionHistory,
  redo,
  undo,
}
export type { ActionHistory, ActionHistoryRecord, DeleteDraftActionHistory }
